import { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { AddConditionButton } from '../add-condition-button';
import { ConnectorThread } from '../connector';
import { IOption } from '../dropdown';
import { QuestionLabel } from '../question-label';
import { AmlQuestionWrapper } from './aml-question-field';
import { capitalize } from 'utils';
import { NodesState, isComplexViewOnlyState } from 'views/complex-onboarding-flow/stores/states';

export interface IRenderQuestion {
	stepId: string;
	nodeId: string;
	blockId: string;
}
export const RenderAmlQuestion: FC<IRenderQuestion> = ({
	stepId,
	nodeId,
	blockId,
}) => {
	// Global variables
	const [connect, setConnect] = useState('and');
	const isComplexViewOnly = useRecoilValue(isComplexViewOnlyState);
	const [nodes, setNodes] = useRecoilState(NodesState);

	const { conditions: aml } = useMemo(
		() => nodes.find((el: any) => el.id === nodeId),
		[nodes, nodeId]
	);


	const handleChangeSelect = useCallback(
		(val: IOption) => {

				setNodes((prev: any) => {
					const prevObj = JSON.parse(JSON.stringify(prev));
					const findIndex = prevObj.findIndex((el: any) => el.id === nodeId);
					if (findIndex !== -1) {
						const index = prevObj[findIndex].conditions.findIndex(
							(el: any) => el.currentId === nodeId && el.blockId === blockId
						);

						if (index !== -1) {
							const prevValue = prevObj[findIndex].conditions[index][connect];
							const payload = {
								[val.value]: prevValue,
							};
							Object.assign(prevObj[findIndex].conditions[index], payload);
							if (val.value !== connect) {
								delete prevObj[findIndex].conditions[index][connect];
							}
						}
					}

					return prevObj;
				});

				setConnect(val.value);
		},
		[setNodes, nodeId, blockId, connect]
	);

	const addNewQuestion = useCallback(() => {
		setNodes((prev: any) => {
			const prevObj = JSON.parse(JSON.stringify(prev));
			const findIndex = prevObj.findIndex((el: any) => el.id === nodeId);
			if (findIndex !== -1) {
				const index = prevObj[findIndex]?.conditions?.findIndex(
					(el: any) => el.currentId === nodeId && el.blockId === blockId
				);
				if (index !== -1) {
					prevObj[findIndex]?.conditions[index]?.[connect]?.push({});
				}
			}

			return prevObj;
		});
	}, [setNodes, nodeId, blockId, connect]);

	const kycQuestionList = useMemo(
		() =>
			aml?.find(
				(el: any) => el.currentId == nodeId && el.blockId === blockId
			)?.[connect] ?? [{}],
		[aml, connect, nodeId, blockId]
	);

	const onRemoveBlock = useCallback(
		(blockId: string) => () => {
			setNodes((prev: any) => {
				const prevObj = JSON.parse(JSON.stringify(prev));
				const findIndex = prevObj.findIndex((el: any) => el.id === nodeId);
				if (findIndex !== -1) {
					prevObj[findIndex].conditions = prevObj[
						findIndex
					]?.conditions?.filter((el: any) => el.blockId !== blockId);
				}
				return prevObj;
			});
		},
		[setNodes, nodeId]
	);

	useEffect(() => {
		if (!isComplexViewOnly && kycQuestionList?.length === 0) addNewQuestion();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const questionProps = {
		stepId: stepId,
		nodeId: nodeId,
		blockId: blockId,
		questionLen: kycQuestionList?.length,
		connect: connect,
	};

	const defaultThenValue =
		aml?.find((el: any) => el.currentId == nodeId && el.blockId === blockId)
			?.then ?? '';

	useEffect(() => {
		const data = aml?.find(
			(el: any) => el.currentId == nodeId && el.blockId === blockId
		);
		if (Object.keys(data ?? {}).includes('or')) {
			setConnect('or');
		} else {
			setConnect('and');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className="question-list">
			<QuestionLabel blockId={blockId} onRemoveBlock={onRemoveBlock} />
			{kycQuestionList?.map((_: any, index: number) => {
				return (
					<Fragment key={`condition_${index.toString()}`}>
						<AmlQuestionWrapper
							{...questionProps}
							index={index}
							conditionId={`condition_${index}`}
							key={`${_.param}_${_.operand}_${blockId}_${nodeId}`}
							questionData={_}
							defaultThenValue={defaultThenValue}
						/>
						{kycQuestionList?.length !== index + 1 && (
							<ConnectorThread
								handleChangeSelect={handleChangeSelect}
								value={{ label: capitalize(connect), value: connect }}
								// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
								defaultValue={{ label: capitalize(connect), value: connect }}
							/>
						)}
					</Fragment>
				);
			})}
			<AddConditionButton onClick={addNewQuestion} />
		</div>
	);
};
